import React, { useEffect, useState } from 'react';
import { Card, TimelineItemsContainer } from '../../styled';
import { Poppins, Spacer, TimelineSkeleton, StyledLink } from 'src/common';
import { FiltersProvider, useFiltersState } from '../../Filters/state';
import { Filters } from '../../Filters';
import { FModal } from '../../Filters/comps';
import { queryCache, useQuery } from 'react-query';
import { QKeys } from 'src/api/types';
import { getTimelineActions } from 'src/api/dashboards';
import { SelectionChips } from '../SelectionChips';
import { ActivityData, TimelineEvent } from 'src/api/types/dashboard';
import { splitEventsByDateRange } from '../../util';
import { parseEvents } from '../../Events/util';
import { motion } from 'framer-motion';
import { ShowMyActivitySwitch } from '../ShowMyActivitySwitch';
import { ExportActionsCSV, Section } from './comps';
import { ActionText } from 'src/components/QuickActionsMenu/comps';
import { useAuth } from 'src/state/auth';
import _ from 'lodash';
import moment from 'moment';

const RootComponent = () => {
  const [ranges, setRanges] = useState<{
    overdue: TimelineEvent[];
    range1: TimelineEvent[];
    range2: TimelineEvent[];
    range3: TimelineEvent[];
  }>({ overdue: [], range1: [], range2: [], range3: [] });
  const [canRender, setCanRender] = useState(false);
  const { filters, setFilters, setIsInit, isInit } = useFiltersState();
  const { features } = useAuth();

  useEffect(() => {
    getTimelineActions().then((res) => {
      setFilters(res.filters);
      queryCache.setQueryData([QKeys.DashboardTimelineActions, { filters: res.filters }], res);
      setIsInit(true);
    });
  }, []);

  const {
    data: res,
    isLoading,
    error,
  } = useQuery([QKeys.DashboardTimelineActions, { filters }], () => getTimelineActions({ filters }), {
    enabled: isInit,
    onSuccess: () => setCanRender(true),
  });

  useEffect(() => {
    if (res) {
      setRanges(splitEventsByDateRange(res.data));
    }
  }, [res]);

  const exportIds = [...ranges.overdue, ...ranges.range1, ...ranges.range2, ...ranges.range3]
    .map((el) => _.get(el, 'data.action.id'))
    .filter((el) => !!el) as string[];

  return (
    <>
      <FModal initialFilters={res?.filters} owners hideIncidents={true} />
      <Card className="styled-scroll styled-scroll--rounded">
        <div className="controls">
          <div className="controls__left">
            <Poppins weight={600} px={22}>
              Upcoming actions
            </Poppins>
          </div>
          <div className="controls__right">
            <ShowMyActivitySwitch label="Show my actions" />
            <Filters owners />
            {features['app.events'] && <ExportActionsCSV exportIds={exportIds} />}
          </div>
        </div>
        <SelectionChips owners hideIncidents />
        <Spacer $px={10} />
        <TimelineItemsContainer>
          {(isLoading && !res?.data) || !canRender ? (
            <TimelineSkeleton />
          ) : (
            <>
              {res && (
                <motion.div
                  className="upcoming"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <Section title="OVERDUE" ranges={ranges.overdue} id="overdue" overdue />
                  <Section title="NEXT 7 DAYS" ranges={ranges.range1} id="range1" />
                  <Section title="NEXT 30 DAYS" ranges={ranges.range2} id="range2" />
                  <Section title="NEXT 90 DAYS" ranges={ranges.range3} id="range3" />
                </motion.div>
              )}
            </>
          )}

          {!!error && !isLoading && (
            <Poppins px={14} color="error">
              {/* @ts-ignore */}
              {error.message || 'Something went wrong'}
            </Poppins>
          )}
          <Spacer $px={15} />
        </TimelineItemsContainer>
      </Card>
    </>
  );
};

interface UpcomingActionsWidgetRenderProps {
  activityData: ActivityData[];
}

export const UpcomingActionsWidgetRender: React.FC<UpcomingActionsWidgetRenderProps> = ({ activityData }) => {
  const ranges = splitEventsByDateRange(activityData);
  const allEvents = [...ranges.overdue, ...ranges.range1, ...ranges.range2, ...ranges.range3];
  const limitedEvents = allEvents.slice(0, 4);

  return (
    <FiltersProvider type="actions">
      <TimelineItemsContainer $widget>
        {limitedEvents.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <Poppins px={14} color="cflowerBlue">
              No upcoming actions
            </Poppins>
          </div>
        ) : (
          <motion.div
            className="upcoming"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            style={{ height: '100%' }}
          >
            <div className="upcoming__item">
              {limitedEvents.map((event) => {
                const eventDate = moment(event.event_date).startOf('day');
                const today = moment().startOf('day');
                const isToday = eventDate.diff(today, 'days') === 0;

                return parseEvents({
                  events: [event],
                  shownDate: true,
                  truncate: true,
                  isToday,
                })?.[0];
              })}
            </div>

            <StyledLink
              to="/timeline"
              $color="gp1"
              style={{ marginTop: 'auto', textDecoration: 'none', marginRight: 'auto' }}
            >
              <ActionText>View all</ActionText>
            </StyledLink>
          </motion.div>
        )}
      </TimelineItemsContainer>
    </FiltersProvider>
  );
};

const UpcomingActions = () => {
  return (
    <FiltersProvider type="actions">
      <RootComponent />
    </FiltersProvider>
  );
};

export default UpcomingActions;
